<script>
  export default { name: 'MeetingTranscript' }
</script>
<script setup>
  import { computed, onActivated, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import dayjs from 'dayjs'
  import { useMeetingTranscript } from './composables/useMeetingTranscript'
  import TranscriptList from './components/meetingTranscript/TranscriptList.vue'
  import OriginalAudioPlayer from './components/meetingTranscript/OriginalAudioPlayer.vue'
  import { activeSentence, sentenceRange } from './components/meetingTranscript/transcriptTime'

  const route = useRoute()
  const router = useRouter()
  const meetingCode = computed(() => String(route.query.meetingCode || ''))
  const meetingName = computed(() => route.query.meetingName || meetingCode.value)
  const {
    sessions,
    session,
    capture,
    keyword,
    schedule,
    schedules,
    items,
    cursor,
    playback,
    loading,
    sessionsLoading,
    playbackLoading,
    error,
    sessionError,
    playerKey,
    seekRequest,
    initialize,
    selectCapture,
    loadPage,
    refreshPlayback,
    prepareAudio,
    seekSentence,
  } = useMeetingTranscript(meetingCode)
  const positionMs = ref(0)
  const following = ref(true)
  const playing = ref(false)
  const activeId = computed(() => activeSentence(items.value, positionMs.value, playback.value))
  const label = (item, index) =>
    item.captureSessionId
      ? `场次 ${index + 1}${item.firstRecordedAtMs ? ' · ' + dayjs(item.firstRecordedAtMs).format('MM-DD HH:mm') : ''}`
      : '历史记录'
  watch(playerKey, () => {
    positionMs.value = 0
    following.value = true
    playing.value = false
  })
  // Follow playback across page boundaries, including a scrub to a later unloaded sentence.
  watch([positionMs, loading, following], () => {
    if (!following.value || loading.value || error.value || keyword.value || schedule.value || activeId.value) return
    const ranges = items.value.map((item) => sentenceRange(item, playback.value)).filter(Boolean)
    if (ranges.length && positionMs.value < Math.min(...ranges.map((range) => range.start))) {
      loadPage(false)
    } else if (
      cursor.value &&
      (ranges.length ? positionMs.value >= Math.max(...ranges.map((range) => range.end)) : playing.value)
    ) {
      loadPage(true)
    }
  })
  let activated = false
  onActivated(() => {
    if (activated) initialize()
    activated = true
  })
</script>

<template>
  <section class="transcript-page">
    <header class="page-heading">
      <el-button type="text" @click="router.back()">返回</el-button>
      <div>
        <div class="meeting-name">{{ meetingName }}</div>
        <h1 class="page-title">原声逐字播放</h1>
      </div>
    </header>
    <div v-loading="sessionsLoading" class="transcript-workspace">
      <div v-if="sessionError" class="status-row">
        <span>{{ sessionError }}</span>
        <el-button size="small" @click="initialize">重试</el-button>
      </div>
      <el-empty v-else-if="!sessionsLoading && !sessions.length" description="暂无逐字稿" />
      <template v-if="sessions.length">
        <div class="filters">
          <el-select aria-label="录制场次" class="session-select" :model-value="capture" @change="selectCapture">
            <el-option
              v-for="(item, index) in sessions"
              :key="item.captureSessionId || 'legacy'"
              :label="label(item, index)"
              :value="item.captureSessionId || ''"
            />
          </el-select>
          <el-select v-model="schedule" aria-label="日程" clearable placeholder="全部日程" @change="loadPage(false)">
            <el-option v-for="code in schedules" :key="code" :label="code" :value="code" />
          </el-select>
          <el-input
            v-model="keyword"
            aria-label="搜索原文"
            class="search-input"
            clearable
            maxlength="100"
            placeholder="搜索原文"
            @clear="loadPage(false)"
            @keyup.enter="loadPage(false)"
          />
          <el-button type="primary" @click="loadPage(false)">搜索</el-button>
        </div>
        <div class="list-heading">
          <span>
            原文
            <span class="count">{{ session?.sentenceCount || 0 }} 条</span>
          </span>
          <el-button v-if="playback.status === 'AVAILABLE'" size="small" type="text" @click="following = true">
            {{ following ? '跟随播放中' : '回到当前播放位置' }}
          </el-button>
        </div>
        <div v-if="error" class="status-row">
          <span>{{ error }}</span>
          <el-button size="small" @click="loadPage(!!items.length)">重试</el-button>
        </div>
        <TranscriptList
          :following="following"
          :items="items"
          :playback="playback"
          :position-ms="positionMs"
          @browse="following = false"
          @seek="
            (item, range) => {
              following = true
              seekSentence(item, range)
            }
          "
        />
        <div class="pagination">
          <el-button v-if="cursor" :loading="loading" @click="loadPage(true)">加载更多</el-button>
          <span v-else-if="loading">原文加载中…</span>
          <span v-else>{{ items.length ? '已到原文末尾' : '暂无匹配原文' }}</span>
        </div>
        <OriginalAudioPlayer
          :key="playerKey"
          :loading="playbackLoading"
          :playback="playback"
          :seek-request="seekRequest"
          @playing="playing = $event"
          @position="positionMs = $event"
          @prepare="prepareAudio"
          @refresh="refreshPlayback"
        />
      </template>
    </div>
  </section>
</template>

<style scoped>
  .transcript-page {
    box-sizing: border-box;
    padding: 16px;
    background: #f6f8fa;
    height: calc(100vh - 220px);
    min-height: 440px;
    display: flex;
    flex-direction: column;
  }
  .page-heading {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-bottom: 16px;
    flex-shrink: 0;
  }
  .meeting-name {
    color: #909399;
    font-size: 13px;
    margin-bottom: 6px;
  }
  .page-title {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    color: #303133;
  }
  .transcript-workspace {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 4px;
  }
  .filters {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    padding: 16px;
    flex-shrink: 0;
    border-bottom: 1px solid #ebeef5;
  }
  .session-select {
    min-width: 220px;
  }
  .search-input {
    width: 260px;
    margin-left: auto;
  }
  .list-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 28px 8px;
    font-size: 14px;
    color: #303133;
  }
  .count {
    color: #909399;
    font-size: 12px;
    margin-left: 12px;
  }
  .status-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    color: #e6a23c;
  }
  .pagination {
    padding: 12px;
    flex-shrink: 0;
    text-align: center;
    font-size: 12px;
    color: #909399;
  }
  @media (max-width: 768px) {
    .transcript-page {
      padding: 12px;
      height: auto;
    }
    .filters {
      padding: 16px;
    }
    .search-input {
      margin-left: 0;
      width: 100%;
    }
  }
</style>
